import classNames from "classnames";
import React from "react";
import { Step } from "semantic-ui-react";
import { changeLifecycle } from "utilities/esigningUtils";

export type LifecycleStepsType = Exclude<server.dto.DocumentLifeCycles, "unknown" | "discarded">;

export interface StepProps {
  validate?: () => boolean | Promise<boolean>
  onClick?: () => void
  disabled: boolean

  // This grays out the step, but it's still clickable (unlike 'disabled')
  hasErrors?: boolean
};
export type StepOptions = Record<LifecycleStepsType, StepProps>;

interface Props {
  activeStep: LifecycleStepsType
  stepOptions: StepOptions
  overDue: boolean
  documentType: server.dto.DocumentTypes
  documentId: number
  draftLabel?: string
  reviewLabel?: string
  pendingLabel?: string
  completedLabel?: string
}

const LifecycleSteps = (props: Props) => {
  const { draft, review, pending, completed } = props.stepOptions;
  const { activeStep } = props;
  const [isLoading, setIsLoading] = React.useState(false);

  const draftLabel = props.draftLabel || RESX.DocumentLifeCycle.Draft;
  const reviewLabel = props.reviewLabel || RESX.DocumentLifeCycle.Review;
  const pendingLabel = props.pendingLabel || RESX.DocumentLifeCycle.OutForSignature;
  const completedLabel = props.completedLabel || RESX.DocumentLifeCycle.Completed;

  const createClickHandler = (lifecycle: LifecycleStepsType) => {
    if (activeStep === lifecycle) return undefined;

    return async () => {
      const lifecycleOptions = props.stepOptions[lifecycle];

      if (lifecycleOptions.onClick) {
        if (lifecycleOptions.validate) {
          const isValid = await lifecycleOptions.validate();
          if (!isValid) return;
        }
        lifecycleOptions.onClick();
        return;
      }

      if (isLoading) return;
      if (lifecycle === props.activeStep) return;

      setIsLoading(true);
      const res = await changeLifecycle(props.documentType, lifecycle, props.documentId, lifecycleOptions.validate);
      if (!res) {
        setIsLoading(false);
      }
    };
  };

  const pendingColor = props.overDue ? "red" : "orange";

  const draftClass = classNames({ s1_gray: draft.hasErrors || draft.disabled });
  const reviewClass = classNames({ s1_gray: review.hasErrors || review.disabled });
  const pendingClass = classNames({ s1_gray: pending.hasErrors || pending.disabled });
  const completedClass = classNames({ s1_gray: completed.hasErrors || completed.disabled });

  return <div><Step.Group widths={4}>
    <Step onClick={createClickHandler("draft")} active={activeStep === "draft"} className={activeStep === "draft" ? "dark" : undefined} disabled={draft.disabled}>
      <Step.Content>
        <Step.Title className={draftClass}>{draftLabel}</Step.Title>
      </Step.Content>
    </Step>
    <Step onClick={createClickHandler("review")} active={activeStep === "review"} className={activeStep === "review" ? "purple" : undefined} disabled={review.disabled}>
      <Step.Content>
        <Step.Title className={reviewClass}>{reviewLabel}</Step.Title>
      </Step.Content>
    </Step>
    <Step onClick={createClickHandler("pending")} active={activeStep === "pending"} className={activeStep === "pending" ? pendingColor : undefined} disabled={pending.disabled}>
      <Step.Content>
        <Step.Title className={pendingClass}>{pendingLabel}</Step.Title>
      </Step.Content>
    </Step>
    <Step onClick={createClickHandler("completed")} active={activeStep === "completed"} className={activeStep === "completed" ? "green" : undefined} disabled={completed.disabled}>
      <Step.Content>
        <Step.Title className={completedClass}>{completedLabel}</Step.Title>
        <Step.Description></Step.Description>
      </Step.Content>
    </Step>
  </Step.Group></div>;
};

export default LifecycleSteps;